/**
 * ExpandingHeaderSearch — internal helper for SDataTable's inline header
 * search pattern.
 *
 * Idle state: renders the column label next to a small SearchIcon button.
 * Click the icon → expands into a `TextField` that fills the header cell
 * (label hides). `onBlur` collapses if empty; `Escape` clears and collapses
 * unconditionally. When a query is active but the input is collapsed, the
 * icon shows a primary-coloured dot badge and a Tooltip reveals the query
 * on hover.
 *
 * Controlled: parent owns the `value` (typically wired to MRT's filter
 * state via `column.setFilterValue` or `table.setGlobalFilter`).
 *
 * Not exported from the module barrel — SDataTable consumes it privately
 * via a column's `Header` render prop.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

export interface ExpandingHeaderSearchProps {
  label: React.ReactNode;
  placeholder?: string;
  value: string;
  onChange: (next: string) => void;
}

export function ExpandingHeaderSearch({
  label,
  placeholder = 'Search...',
  value,
  onChange,
}: ExpandingHeaderSearchProps) {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const active = value.length > 0;

  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
  }, [expanded]);

  const handleBlur = () => {
    if (value === '') {
      setExpanded(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Inside an MRT header cell the th listens for cell keyboard shortcuts
    // (enableKeyboardShortcuts, default ON) and hijacks Arrow/Home/End to
    // move cell focus — keystrokes typed into the input must not leak out.
    e.stopPropagation();
    if (e.key === 'Escape') {
      onChange('');
      setExpanded(false);
    }
  };

  if (!expanded) {
    return (
      <Stack direction="row" alignItems="center" gap={0.5} role="search">
        <Box component="span">{label}</Box>
        <Tooltip
          title={active ? value : ''}
          arrow
          disableHoverListener={!active}
        >
          <IconButton
            size="small"
            aria-label="Search"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(true);
            }}
            sx={{
              position: 'relative',
              color: 'text.secondary',
              ...(active && {
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  pointerEvents: 'none',
                },
              }),
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Box sx={{ width: '100%' }} role="search" onClick={(e) => e.stopPropagation()}>
      <TextField
        inputRef={inputRef}
        autoFocus
        size="small"
        fullWidth
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        inputProps={{
          'aria-label': placeholder,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search"
                edge="end"
                // preventDefault on mouseDown keeps focus on the TextField so
                // `onBlur` does NOT fire between the click and our onChange —
                // without this, the blur handler sees stale (non-empty) value
                // and briefly leaves the input in a weird empty-blurred state.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onChange('')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: { fontSize: '0.8125rem' },
        }}
      />
    </Box>
  );
}

export default ExpandingHeaderSearch;
